import React, { useEffect, useRef, useState } from 'react';

const COVERAGE_OPTIONS = [
  'Commercial Insurance',
  'Home Insurance',
  'Personal Insurance',
  'Not sure yet',
];

const initialForm = {
  name: '',
  phone: '',
  email: '',
  coverage: COVERAGE_OPTIONS[0],
  message: '',
  marketingConsent: false,
};

const QuoteRequest = () => {
  const [form, setForm] = useState(initialForm);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');
  const [coverageOpen, setCoverageOpen] = useState(false);
  const coverageMenuRef = useRef(null);

  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      setTurnstileError('The spam protection is not configured yet.');
      return undefined;
    }

    const renderWidget = () => {
      if (window.turnstile && document.getElementById('turnstile-widget')) {
        window.turnstile.render('#turnstile-widget', {
          sitekey: siteKey,
          callback: setTurnstileToken,
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => {
            setTurnstileToken('');
            setTurnstileError('Unable to load spam protection. Please try again.');
          },
        });
      }
    };

    const existingScript = document.querySelector('script[data-turnstile-script]');
    if (existingScript) {
      renderWidget();
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.onload = renderWidget;
    document.head.appendChild(script);

    return () => {
      setTurnstileToken('');
    };
  }, []);

  useEffect(() => {
    const closeCoverageMenu = (event) => {
      if (coverageMenuRef.current && !coverageMenuRef.current.contains(event.target)) {
        setCoverageOpen(false);
      }
    };

    document.addEventListener('mousedown', closeCoverageMenu);
    return () => document.removeEventListener('mousedown', closeCoverageMenu);
  }, []);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setTurnstileError('Please complete the spam protection check.');
      return;
    }

    let response;
    try {
      response = await fetch('/api/submit-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, turnstileToken }),
      });
    } catch {
      setTurnstileError('Unable to send the inquiry right now. Please try again.');
      return;
    }

    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      setTurnstileError(result.error || 'The inquiry could not be sent. Please try again.');
      setTurnstileToken('');
      return;
    }

    setForm(initialForm);
    setTurnstileToken('');
    setTurnstileError('');
    window.alert('Thank you. Your inquiry has been sent.');
  };

  const inputClasses =
    'w-full border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:border-navy';
  const labelClasses = 'block text-xs font-semibold tracking-widest text-gray-500 mb-1';

  return (
    <div id="inquiry" className="bg-white pt-16 lg:pt-20 pb-10 lg:pb-12 border-t border-gray-200">
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <div className="mb-12 text-center">
          <p className="text-base font-semibold tracking-wide text-gold">Start a Conversation</p>
          <p className="mt-2 font-serif text-4xl font-semibold text-navy-dark">Request Information</p>
          <p className="mt-4 text-lg text-gray-600">
            Share your questions or interests and we will connect you with the right person at McLean Insurance Brokers.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-300 p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className={labelClasses} htmlFor="name">Full Name</label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="Enter your name"
                onChange={handleChange}
                value={form.name}
                className={inputClasses}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="phone">Phone</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                placeholder="e.g. +501 000-0000"
                value={form.phone}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>
          </div>

          <div>
            <label className={labelClasses} htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              value={form.email}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>

          <div ref={coverageMenuRef} className="relative">
            <label className={labelClasses} id="coverage-label">Coverage Type</label>
            <button
              type="button"
              id="coverage"
              aria-haspopup="listbox"
              aria-expanded={coverageOpen}
              aria-labelledby="coverage-label"
              onClick={() => setCoverageOpen((open) => !open)}
              className={`${inputClasses} flex items-center justify-between bg-white text-left`}
            >
              <span>{form.coverage}</span>
              <svg
                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                  coverageOpen ? 'rotate-180' : ''
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.51a.75.75 0 01-1.08 0l-4.25-4.51a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {coverageOpen && (
              <div
                role="listbox"
                aria-labelledby="coverage-label"
                className="absolute left-0 right-0 z-10 mt-1 overflow-hidden border border-gray-300 bg-white shadow-lg"
              >
                {COVERAGE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={form.coverage === option}
                    onClick={() => {
                      setForm((prev) => ({ ...prev, coverage: option }));
                      setCoverageOpen(false);
                    }}
                    className={`block w-full px-3 py-2 text-left text-gray-900 hover:bg-gray-100 ${
                      form.coverage === option ? 'bg-gray-50 font-semibold' : ''
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelClasses} htmlFor="message">Tell Us More</label>
            <textarea
              id="message"
              name="message"
              rows={4}
              placeholder="Tell us how we can help or what information you would like to receive."
              value={form.message}
              onChange={handleChange}
              className={inputClasses}
            />
          </div>

          <label className="flex items-start gap-3 text-sm text-gray-600">
            <input
              type="checkbox"
              name="marketingConsent"
              required
              checked={form.marketingConsent}
              onChange={handleChange}
              className="mt-1 h-4 w-4 accent-navy"
            />
            <span>
              I agree that McLean Insurance Brokers may use my information to respond to this inquiry
              and send me related updates, promotions, and offers.
            </span>
          </label>

          <div>
            <div id="turnstile-widget" />
            {turnstileError && <p className="mt-2 text-sm text-red-600" role="alert">{turnstileError}</p>}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="submit"
              className="w-full sm:w-auto bg-navy hover:bg-navy-dark text-white text-sm font-semibold tracking-wider px-8 py-3 transition-colors duration-200"
            >
              Send Inquiry
            </button>
            <a
              href="https://wa.me/5016237942?text=Hello%20I%20would%20like%20to%20get%20more%20information%20about%20your%20insurance%20services.%20How%20can%20I%20get%20started%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 bg-[#25D366] px-8 py-3 text-sm font-semibold tracking-wider text-white transition-colors duration-200 hover:bg-[#1ebe5d] sm:w-auto"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.52 3.48A11.82 11.82 0 0 0 12.08 0C5.55 0 .24 5.31.24 11.84c0 2.09.55 4.13 1.6 5.93L.14 24l6.38-1.67a11.84 11.84 0 0 0 5.56 1.39h.01c6.53 0 11.84-5.31 11.84-11.84 0-3.17-1.23-6.15-3.41-8.4ZM12.09 21.7h-.01a9.84 9.84 0 0 1-5.02-1.37l-.36-.21-3.79.99 1.01-3.69-.23-.38a9.83 9.83 0 0 1-1.5-5.2C2.19 6.41 6.61 2 12.09 2c2.66 0 5.16 1.04 7.04 2.93a9.88 9.88 0 0 1 2.91 7.05c0 5.48-4.46 9.72-9.95 9.72Zm5.4-7.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5a9.08 9.08 0 0 1-1.68-2.09c-.18-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.21 5.09 4.5.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z" />
              </svg>
              Contact Us
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteRequest;
